import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import { jsPDF } from 'jspdf';
import NotificationBell from '../../habitat/components/NotificationBell'; // ✅ Ajout de la cloche
import { 
  Briefcase, LayoutDashboard, User, CreditCard, LogOut, 
  MapPin, Clock, Star, Lock, CheckCircle, Wrench, AlertTriangle, X, Send, Loader2, Target, ChevronRight, ShieldCheck, Calculator, HelpCircle, UploadCloud, ImageIcon, Navigation,
  DollarSign, Calendar, FileSpreadsheet, Archive, FileDown
} from 'lucide-react';

export default function ProDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('marketplace');
  const [profile, setProfile] = useState<any>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [myJobs, setMyJobs] = useState<any[]>([]); 
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  const [trackingTicketId, setTrackingTicketId] = useState<string | null>(null);

  const [profileData, setProfileData] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    logo_url: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [reportYear, setReportYear] = useState<number>(new Date().getFullYear());
  const [generatingReport, setGeneratingReport] = useState(false);

  const amountHT = Number(quoteAmount) || 0;
  const tps = amountHT * 0.05;
  const tvq = amountHT * 0.09975;
  const totalTTC = amountHT + tps + tvq;

  useEffect(() => {
    fetchData();
    checkStripeRedirect();
  }, []);

  const fetchData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login?type=forfeopro');
      return;
    }

    const { data: proData } = await supabase.from('forfeo_pros').select('*').eq('id', user.id).single();
    if (proData) {
      setProfile(proData);
      setProfileData({
        company_name: proData.company_name || '',
        email: proData.email || '',
        phone: proData.phone || '',
        address: proData.address || '',
        tax_number: proData.tax_number || '',
        logo_url: proData.logo_url || ''
      });
      if (proData.subscription_status !== 'active') {
        setShowPaywall(true);
      }
    }

    const { data: openTickets, error } = await supabase
      .from('habitat_maintenance_tickets')
      .select('*, listing:habitat_listings(address, city)') 
      .in('status', ['searching_pro', 'in_progress']) 
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Erreur de récupération des requêtes:", error);
    } else if (openTickets) {
      const landlordIds = [...new Set(openTickets.map(t => t.landlord_id).filter(Boolean))];
      let landlords: any[] = [];
      if (landlordIds.length > 0) {
        const { data: profilesData } = await supabase.from('habitat_profiles').select('id, full_name').in('id', landlordIds);
        if (profilesData) landlords = profilesData;
      }

      const enrichedTickets = openTickets.map(ticket => ({
        ...ticket,
        landlord: landlords.find(l => l.id === ticket.landlord_id) || { full_name: 'Propriétaire Forfeo' }
      }));

      setTickets(enrichedTickets);
      
      const activeTracking = enrichedTickets.find(t => t.tracking_status === 'en_route' && t.pro_id === user.id);
      if (activeTracking) setTrackingTicketId(activeTracking.id);
    }

    const { data: jobs } = await supabase
      .from('habitat_maintenance_tickets')
      .select('*, listing:habitat_listings(address, city)')
      .eq('pro_id', user.id)
      .in('status', ['in_progress', 'resolved'])
      .order('updated_at', { ascending: false });

    if (jobs) {
      const landlordIds = [...new Set(jobs.map(t => t.landlord_id).filter(Boolean))];
      let landlords: any[] = [];
      if (landlordIds.length > 0) {
        const { data: profilesData } = await supabase.from('habitat_profiles').select('id, full_name').in('id', landlordIds);
        if (profilesData) landlords = profilesData;
      }
      const enrichedJobs = jobs.map(job => ({
        ...job,
        landlord: landlords.find(l => l.id === job.landlord_id) || { full_name: 'Client Forfeo' }
      }));
      setMyJobs(enrichedJobs);
    }
    
    setLoading(false);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.from('forfeo_pros').update(profileData).eq('id', user.id);
    
    setSavingProfile(false);
    if (!error) {
      setProfile({ ...profile, ...profileData });
      alert("✅ Profil entreprise mis à jour avec succès !");
    } else {
      alert("Erreur lors de la mise à jour : " + error.message);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setUploadingLogo(true);
    const file = e.target.files[0];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/logo_${Math.random()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage.from('habitat_public').upload(fileName, file);
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage.from('habitat_public').getPublicUrl(fileName);
      
      setProfileData(prev => ({ ...prev, logo_url: publicUrl }));
      await supabase.from('forfeo_pros').update({ logo_url: publicUrl }).eq('id', user.id);
      
    } catch (err: any) {
      alert("Erreur lors du téléversement du logo : " + err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const startTracking = async (ticket: any) => {
    if (!navigator.geolocation) {
      alert("Votre appareil ne supporte pas la géolocalisation.");
      return;
    }

    if (window.confirm(`Voulez-vous indiquer que vous êtes en route vers le ${ticket.listing?.address} ? Le propriétaire et le locataire verront votre position sur leur carte.`)) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        
        await supabase
          .from('habitat_maintenance_tickets')
          .update({ tracking_status: 'en_route', pro_lat: latitude, pro_lng: longitude })
          .eq('id', ticket.id);
          
        setTrackingTicketId(ticket.id);
        alert("✅ GPS activé ! Votre position s'affiche maintenant sur le tableau de bord du client.");
        fetchData();

        navigator.geolocation.watchPosition(async (pos) => {
          await supabase.from('habitat_maintenance_tickets').update({
            pro_lat: pos.coords.latitude, pro_lng: pos.coords.longitude
          }).eq('id', ticket.id);
        }, null, { enableHighAccuracy: true, maximumAge: 10000 });

      }, (error) => {
        alert("Impossible de vous localiser. Avez-vous autorisé l'accès au GPS dans votre navigateur ?");
      }, { enableHighAccuracy: true });
    }
  };

  const checkStripeRedirect = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('success') === 'true') {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('forfeo_pros').update({ subscription_status: 'active' }).eq('id', user.id);
        setProfile((prev: any) => ({ ...prev, subscription_status: 'active' }));
        setShowPaywall(false);
        window.history.replaceState(null, '', window.location.pathname);
      }
    } else if (queryParams.get('canceled') === 'true') {
      setShowPaywall(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login?type=forfeopro');
  };

  const checkoutStripe = async () => {
    setIsProcessingPayment(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      try {
        const returnUrl = window.location.origin + window.location.pathname;
        const { data, error } = await supabase.functions.invoke('stripe-pro-checkout', {
          body: { userId: user.id, email: user.email, returnUrl: returnUrl }
        });
        if (error) throw error;
        if (data?.url) window.location.href = data.url;
      } catch (err) {
        console.error("Erreur Stripe:", err);
        alert("Une erreur est survenue lors de la connexion à Stripe.");
        setIsProcessingPayment(false);
      }
    }
  };

  const handleContactLandlord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket || !contactMessage || amountHT <= 0) {
      alert("Veuillez entrer un montant valide et un message.");
      return;
    }
    setIsSending(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Utilisateur non connecté");

      const { data: landlordProfile } = await supabase
        .from('habitat_profiles')
        .select('email')
        .eq('id', selectedTicket.landlord_id)
        .single();

      const landlordEmail = landlordProfile?.email;
      
      if (landlordEmail) {
        const finalQuote = parseFloat(totalTTC.toFixed(2));
        const { error: updateError } = await supabase
          .from('habitat_maintenance_tickets')
          .update({
            pro_id: user.id,
            pro_name: profileData.company_name || 'Artisan Forfeo',
            quote_amount: finalQuote,
            quote_message: contactMessage
          })
          .eq('id', selectedTicket.id);

        if (updateError) throw updateError;

        await supabase.functions.invoke('habitat-send-email', {
          body: { 
            type: 'pro_contact', 
            email: landlordEmail, 
            data: { 
              ticketCategory: selectedTicket.category,
              listingAddress: selectedTicket.listing?.address,
              proName: profileData.company_name,
              proEmail: profileData.email,
              proPhone: profileData.phone || '',
              quoteTotal: finalQuote,
              message: contactMessage
            } 
          }
        });
        
        alert(`✅ Le devis de ${finalQuote}$ a été transmis et sauvegardé !`);
        setSelectedTicket(null);
        setContactMessage('');
        setQuoteAmount('');
        fetchData(); 
      } else {
        alert("Impossible de trouver le contact du propriétaire.");
      }
    } catch (error: any) {
      alert("Erreur lors de l'envoi : " + error.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const yearJobs = myJobs.filter(j => new Date(j.updated_at || j.created_at).getFullYear() === reportYear);

      const doc = new jsPDF();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text(`Forfeo Pros - Rapport Comptable ${reportYear}`, 20, 30);

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Entreprise : ${profileData.company_name || 'Non spécifié'}`, 20, 45);
      doc.text(`Date d'émission : ${new Date().toLocaleDateString()}`, 20, 52);

      doc.setLineWidth(0.5);
      doc.line(20, 60, 190, 60);

      doc.setFont("helvetica", "bold");
      doc.text("Sommaire des revenus de maintenance :", 20, 75);
      doc.setFont("helvetica", "normal");

      let yPos = 85;
      let totalAmount = 0;

      if (yearJobs.length === 0) {
        doc.text("Aucun revenu enregistré via la plateforme cette année-là.", 20, yPos);
      } else {
        yearJobs.forEach((j) => {
           const amount = j.quote_amount ? parseFloat(j.quote_amount).toFixed(2) : '0.00';
           totalAmount += j.quote_amount || 0;

           doc.text(`- ${new Date(j.updated_at || j.created_at).toLocaleDateString()} | ${j.category.toUpperCase()}`, 20, yPos);
           doc.text(`${amount} $`, 170, yPos, { align: 'right' }); 

           doc.setFontSize(9);
           doc.setTextColor(100);
           doc.text(`  Client: ${j.landlord?.full_name || 'Inconnu'} (${j.listing?.address || 'Adresse masquée'})`, 20, yPos + 5);
           doc.setFontSize(12);
           doc.setTextColor(0);
           yPos += 12;

           if (yPos > 270) {
             doc.addPage();
             yPos = 20;
           }
        });

        doc.setLineWidth(0.2);
        doc.line(20, yPos + 5, 190, yPos + 5);
        doc.setFont("helvetica", "bold");
        doc.text("TOTAL DES REVENUS :", 120, yPos + 15);
        doc.text(`${totalAmount.toFixed(2)} $`, 170, yPos + 15, { align: 'right' });
      }

      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text("Ce document est un récapitulatif généré par Forfeo Explore à des fins comptables.", 20, 285);

      doc.save(`Rapport_Comptable_Forfeo_${reportYear}.pdf`);
      alert(`✅ Rapport de l'année ${reportYear} généré et téléchargé avec succès !`);

    } catch (err: any) {
      alert("Erreur lors de la génération du PDF : " + err.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const groupedJobs = myJobs.reduce((acc: any, job: any) => {
    const date = new Date(job.updated_at || job.created_at);
    if (date.getFullYear() !== reportYear) return acc;
    const monthYear = date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
    const capitalizedMonth = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
    if (!acc[capitalizedMonth]) acc[capitalizedMonth] = { total: 0, items: [] };
    acc[capitalizedMonth].items.push(job);
    acc[capitalizedMonth].total += job.quote_amount || 0;
    return acc;
  }, {});

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="animate-spin text-amber-500 w-12 h-12" /></div>;

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col md:flex-row font-sans">
      
      <aside className="w-full md:w-[280px] bg-[#0B1120] text-slate-300 flex flex-col md:h-screen md:sticky md:top-0 shadow-2xl z-40 overflow-hidden">
        <div className="p-6 border-b border-slate-800 shrink-0">
          <h2 className="text-2xl font-black text-white flex items-center gap-2"><Wrench className="text-amber-500"/> Forfeo <span className="text-amber-500 font-light">Pros</span></h2>
        </div>

        <nav className="flex-1 flex md:flex-col p-4 gap-2 overflow-y-auto overflow-x-auto hide-scrollbar">
          <button onClick={() => setActiveTab('marketplace')} className={`flex gap-3 p-4 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='marketplace'?'bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/20':'hover:bg-white/5'}`}><Briefcase size={20}/> Opportunités</button>
          <button onClick={() => setActiveTab('finances')} className={`flex gap-3 p-4 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='finances'?'bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/20':'hover:bg-white/5'}`}><DollarSign size={20}/> Comptabilité</button>
          <button onClick={() => setActiveTab('profile')} className={`flex gap-3 p-4 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='profile'?'bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/20':'hover:bg-white/5'}`}><User size={20}/> Mon Profil</button>
          <button onClick={() => setActiveTab('faq')} className={`flex gap-3 p-4 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab==='faq'?'bg-purple-500/10 text-purple-400':'hover:bg-white/5'}`}><HelpCircle size={20}/> FAQ / Aide</button>
        </nav>
        
        <div className="mt-auto p-4 border-t border-slate-800/50 shrink-0 pb-24 md:pb-6">
          {/* ✅ LA CLOCHE DE NOTIFICATION EST INTÉGRÉE ICI */}
          <div className="px-4 py-3 mb-3 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-between">
            <div className="overflow-hidden">
              <p className="text-[10px] font-black uppercase text-slate-500 mb-1 tracking-widest">Entreprise connectée</p>
              <p className="text-sm font-bold text-white truncate">{profileData.company_name}</p>
            </div>
            <NotificationBell />
          </div>
          
          <button onClick={() => setShowPaywall(true)} className="mb-3 w-full bg-slate-800 hover:bg-slate-700 text-slate-300 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2 border border-slate-700">
            <CreditCard size={12}/> Gérer mon forfait
          </button>
          
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 p-3 text-red-400 text-xs font-bold uppercase rounded-xl hover:bg-red-500/10 border border-red-500/20 transition-colors">
            <LogOut size={16}/> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto h-screen relative">
        <div className="p-6 md:p-10 max-w-6xl mx-auto pb-32">
          
          {activeTab === 'marketplace' && (
            <div className="animate-in fade-in slide-in-from-bottom-4">
              
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-[2.5rem] p-8 md:p-12 mb-10 shadow-2xl relative overflow-hidden border border-slate-700">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 text-amber-500 font-bold text-sm uppercase tracking-widest mb-4">
                    <Target size={18} /> Appels d'offres en direct
                  </div>
                  <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight">Développez votre clientèle</h1>
                  <p className="text-slate-400 font-medium max-w-xl text-sm md:text-base">
                    Répondez aux requêtes de maintenance publiées par les propriétaires Forfeo de votre région et remplissez votre carnet de commandes. Les devis sont automatiquement comptabilisés.
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2"><AlertTriangle className="text-amber-500"/> Chantiers disponibles</h2>
                <div className="bg-amber-100 text-amber-800 px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest shadow-sm">
                  {tickets.length} requêtes
                </div>
              </div>

              {tickets.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-[3rem] p-20 text-center shadow-sm">
                  <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={48} className="text-slate-300" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-800 mb-2">Aucune demande en cours</h3>
                  <p className="text-slate-400 font-medium text-lg">Les propriétaires n'ont publié aucune demande récemment. Revenez plus tard !</p>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-6">
                  {tickets.map(ticket => {
                    const isAccepted = ticket.status === 'in_progress';
                    const isTracking = trackingTicketId === ticket.id;

                    return (
                      <div key={ticket.id} className={`bg-white rounded-[2rem] border p-8 shadow-sm hover:shadow-2xl transition-all duration-300 flex flex-col h-full relative overflow-hidden group ${isAccepted ? 'border-teal-400 bg-teal-50/10' : 'border-slate-200 hover:border-amber-300'}`}>
                        
                        {profile?.subscription_status !== 'active' && !isAccepted && (
                          <div className="absolute inset-0 bg-white/70 backdrop-blur-md z-10 flex items-center justify-center flex-col p-6 text-center">
                            <div className="bg-white p-4 rounded-full shadow-lg mb-4">
                              <Lock className="text-amber-500 w-10 h-10" />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-2">Contenu réservé aux Pros</h3>
                            <p className="text-slate-500 text-sm mb-6 font-medium">Abonnez-vous pour voir les détails et soumettre un devis formel au propriétaire.</p>
                            <button onClick={() => setShowPaywall(true)} className="bg-amber-500 text-slate-900 px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-transform shadow-xl shadow-amber-500/30">
                              Débloquer les chantiers
                            </button>
                          </div>
                        )}

                        <div className="flex justify-between items-start mb-6">
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border shadow-inner shrink-0 ${isAccepted ? 'bg-teal-500 text-white border-teal-600' : ticket.urgency === 'urgente' ? 'bg-red-50 text-red-500 border-red-100' : 'bg-amber-50 text-amber-500 border-amber-100'}`}>
                            {isAccepted ? <CheckCircle size={32}/> : <Wrench size={32} />}
                          </div>
                          <div className="text-right">
                            <span className={`text-[10px] font-black uppercase px-3 py-1.5 rounded-lg tracking-widest ${isAccepted ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-500'}`}>
                              {isAccepted ? 'Devis Accepté !' : ticket.category}
                            </span>
                            <p className="text-xs font-bold text-slate-400 mt-2 flex items-center gap-1 justify-end"><Clock size={12}/> Il y a {Math.floor((new Date().getTime() - new Date(ticket.created_at).getTime()) / (1000 * 3600 * 24))} jours</p>
                          </div>
                        </div>

                        <h4 className="text-xl font-black text-slate-900 mb-3 leading-tight">{ticket.description}</h4>
                        
                        <p className="text-sm font-medium text-slate-500 mb-4 bg-slate-50 p-2 rounded-lg inline-block">
                          👤 Demandé par : <strong className="text-slate-700">{ticket.landlord?.full_name || 'Propriétaire Forfeo'}</strong>
                        </p>
                        
                        {ticket.urgency === 'urgente' && !isAccepted && (
                          <div className="bg-red-50 text-red-600 text-xs font-bold px-3 py-1 rounded-md inline-block w-fit mb-4">🚨 Intervention Urgente Requise</div>
                        )}
                        
                        <div className="mt-auto pt-6 border-t border-slate-100 flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-bold text-slate-600 flex items-center gap-2 mb-1"><MapPin size={16} className={isAccepted ? "text-teal-500" : "text-amber-500"}/> {isAccepted ? ticket.listing?.address : ticket.listing?.city || 'Région masquée'}</p>
                            {isAccepted && <p className="text-xs text-slate-400 ml-6">L'adresse complète est débloquée.</p>}
                          </div>
                          
                          {isAccepted ? (
                            <button 
                              onClick={() => {
                                if (isTracking) {
                                  setTrackingTicketId(null);
                                  supabase.from('habitat_maintenance_tickets').update({ tracking_status: 'none' }).eq('id', ticket.id);
                                } else {
                                  startTracking(ticket);
                                }
                              }} 
                              className={`px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center gap-2 shadow-md ${isTracking ? 'bg-slate-200 text-slate-500' : 'bg-teal-500 text-white hover:bg-slate-900'}`}
                            >
                              {isTracking ? <><CheckCircle size={16}/> Terminer</> : <><Navigation size={16}/> Démarrer</>}
                            </button>
                          ) : (
                            <button 
                              onClick={() => setSelectedTicket(ticket)} 
                              className="bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-amber-500 hover:text-slate-900 transition-colors flex items-center gap-2 shadow-md group-hover:shadow-xl"
                            >
                              Créer un devis <ChevronRight size={16}/>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'finances' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-3xl font-black text-slate-900">Comptabilité Entreprise</h2>
                  <p className="text-slate-500 font-medium">Suivez vos revenus générés sur Forfeo et exportez vos bilans comptables.</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2rem] border border-slate-700 p-8 md:p-10 shadow-xl text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/20 blur-[100px] rounded-full pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-[1.5rem] flex items-center justify-center shrink-0">
                      <FileSpreadsheet size={36} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-black mb-2">Bilan Fiscal {reportYear}</h3>
                      <p className="text-slate-400 text-sm">Générez un rapport PDF détaillé de tous vos chantiers acceptés pour l'année sélectionnée. Le document se téléchargera directement sur votre appareil.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 w-full md:w-auto">
                    <select 
                      value={reportYear}
                      onChange={(e) => setReportYear(Number(e.target.value))}
                      className="p-4 bg-slate-800 rounded-xl border border-slate-600 font-bold text-white outline-none cursor-pointer"
                    >
                      {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button 
                      onClick={handleGenerateReport} 
                      disabled={generatingReport}
                      className="flex-1 md:flex-none bg-amber-500 text-slate-900 px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-amber-400 transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {generatingReport ? <Loader2 className="animate-spin" size={18} /> : <FileDown size={18} />} 
                      Télécharger
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-6 mt-10">
                  <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center"><Calculator size={20}/></div>
                  <h3 className="text-2xl font-black text-slate-900">Revenus de Chantiers (Année {reportYear})</h3>
                </div>

                {Object.keys(groupedJobs).length === 0 ? (
                  <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-16 text-center text-slate-400 shadow-sm">
                    <Archive size={48} className="mx-auto mb-4 opacity-30"/>
                    <p className="font-bold text-lg">Aucun chantier enregistré pour {reportYear}.</p>
                    <p className="text-sm mt-2 max-w-md mx-auto">Acceptez des devis et finalisez des interventions pour que vos revenus s'affichent automatiquement ici.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.keys(groupedJobs).map((monthYear) => {
                      const monthData = groupedJobs[monthYear];
                      return (
                        <div key={monthYear} className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm">
                          <div className="bg-slate-50 p-6 border-b border-slate-200 flex justify-between items-center">
                            <h4 className="font-black text-xl text-slate-800 capitalize flex items-center gap-3"><Calendar size={20} className="text-amber-500"/> {monthYear}</h4>
                            <div className="text-right">
                              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Revenus du mois</p>
                              <p className="text-2xl font-black text-amber-600">{monthData.total.toFixed(2)} $</p>
                            </div>
                          </div>
                          <div className="divide-y divide-slate-100">
                            {monthData.items.map((job: any) => (
                              <div key={job.id} className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 hover:bg-slate-50 transition-colors">
                                <div className="flex items-center gap-5 w-full md:w-auto">
                                  <div className="w-14 h-14 bg-slate-100 text-slate-500 border border-slate-200 rounded-2xl flex items-center justify-center shrink-0">
                                    <Wrench size={24}/>
                                  </div>
                                  <div>
                                    <p className="font-black text-slate-900 text-lg">{job.listing?.address}</p>
                                    <p className="text-sm text-slate-500 font-medium">Intervention : {job.category.toUpperCase()}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-6 w-full md:w-auto justify-between md:justify-end">
                                  <div className="text-right">
                                    <p className="font-black text-2xl text-slate-900">{job.quote_amount.toFixed(2)} $</p>
                                    <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest mt-1">Accepté le {new Date(job.updated_at).toLocaleDateString()}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          )}

          {activeTab === 'profile' && (
            <form onSubmit={handleSaveProfile} className="bg-white p-8 md:p-12 rounded-[3rem] shadow-sm border border-slate-200 max-w-2xl animate-in fade-in slide-in-from-bottom-4">
              <h2 className="text-3xl font-black text-slate-900 mb-8">Profil Entreprise</h2>
              
              <div className="mb-8 pb-8 border-b border-slate-100 flex items-center gap-6">
                <div className="w-24 h-24 rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden relative group shrink-0">
                  {profileData.logo_url ? (
                    <img src={profileData.logo_url} alt="Logo Entreprise" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="text-slate-300 w-8 h-8" />
                  )}
                  <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity ${uploadingLogo ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    {uploadingLogo ? <Loader2 className="animate-spin text-white w-6 h-6"/> : <UploadCloud className="text-white w-6 h-6"/>}
                  </div>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Logo de l'entreprise</h4>
                  <p className="text-xs text-slate-500 mt-1">Image JPG ou PNG. Elle apparaîtra sur vos devis.</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Nom de l'entreprise</label>
                  <input 
                    type="text" 
                    required
                    value={profileData.company_name} 
                    onChange={e => setProfileData({...profileData, company_name: e.target.value})} 
                    className="w-full p-4 rounded-xl border border-slate-200 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-sm" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Courriel de contact</label>
                  <input 
                    type="email" 
                    required
                    value={profileData.email} 
                    onChange={e => setProfileData({...profileData, email: e.target.value})} 
                    className="w-full p-4 rounded-xl border border-slate-200 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-sm" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Numéro de taxes (Optionnel)</label>
                  <input 
                    type="text" 
                    value={profileData.tax_number}
                    onChange={e => setProfileData({...profileData, tax_number: e.target.value})} 
                    placeholder="Ex: TPS 1234... / TVQ 5678..." 
                    className="w-full p-4 rounded-xl border border-slate-200 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-sm" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Téléphone (Public)</label>
                  <input 
                    type="tel" 
                    value={profileData.phone} 
                    onChange={e => setProfileData({...profileData, phone: e.target.value})}
                    placeholder="Ex: 514-000-0000" 
                    className="w-full p-4 rounded-xl border border-slate-200 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-sm" 
                  />
                  <p className="text-[10px] text-slate-400 mt-2 font-medium">C'est ce numéro qui sera transmis aux propriétaires lors de vos soumissions.</p>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Adresse de l'entreprise</label>
                  <textarea 
                    value={profileData.address} 
                    onChange={e => setProfileData({...profileData, address: e.target.value})}
                    placeholder="123 Rue des Artisans, Québec, QC..." 
                    className="w-full p-4 rounded-xl border border-slate-200 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-sm h-24 resize-none" 
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={savingProfile} 
                  className="w-full bg-amber-500 text-slate-900 py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-amber-400 transition-colors shadow-lg mt-6"
                >
                  {savingProfile ? 'Sauvegarde...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'faq' && (
             <div className="animate-in fade-in slide-in-from-bottom-4 max-w-4xl mx-auto">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4"><HelpCircle size={32}/></div>
                  <h2 className="text-3xl font-black text-slate-900">Aide & Facturation</h2>
                  <p className="text-slate-500 font-medium">Comment faire des affaires sur Forfeo Pros.</p>
                </div>
                <div className="space-y-4">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-black text-slate-900 mb-2">Comment fonctionne le calcul des taxes ?</h4>
                    <p className="text-slate-600 text-sm">Lorsque vous remplissez un devis, vous entrez votre tarif Hors Taxes (HT). Le système calcule automatiquement la TPS (5%) et la TVQ (9.975%) pour présenter le total au propriétaire. Ceci aide le propriétaire pour ses déductions fiscales.</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-black text-slate-900 mb-2">Forfeo prend-il une commission ?</h4>
                    <p className="text-slate-600 text-sm">Non, Forfeo Pros fonctionne sur un modèle d'abonnement mensuel (29,99$). Vous conservez 100% de la valeur de vos chantiers et de vos devis.</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-black text-slate-900 mb-2">Comment se fait le paiement des travaux ?</h4>
                    <p className="text-slate-600 text-sm">Actuellement, la plateforme met uniquement en relation le devis officiel et l'acceptation comptable. Le transfert des fonds se fait entre le propriétaire et vous (Virement, Chèque, etc.) selon vos termes.</p>
                  </div>
                </div>
             </div>
          )}
        </div>
      </main>

      {selectedTicket && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden relative border border-slate-200">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-amber-50">
              <div>
                <h3 className="text-2xl font-black text-slate-900">Soumettre un devis</h3>
                <p className="text-sm font-bold text-amber-600 mt-1">Ticket : {selectedTicket.category}</p>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-900 shadow-sm border border-amber-100 transition-colors"><X size={20}/></button>
            </div>
            
            <form onSubmit={handleContactLandlord} className="p-8 space-y-6">
              
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 shadow-sm">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><Calculator size={14}/> Montant estimé (Hors Taxes)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-slate-400">$</span>
                  <input 
                    type="number" 
                    required 
                    min="0"
                    step="0.01"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 p-4 bg-white rounded-xl border border-slate-200 font-black text-xl text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none transition-all"
                  />
                </div>
                
                {amountHT > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-2 text-sm font-bold">
                    <div className="flex justify-between text-slate-500">
                      <span>Montant HT</span><span>{amountHT.toFixed(2)} $</span>
                    </div>
                    <div className="flex justify-between text-slate-500 text-xs">
                      <span>TPS (5.00%)</span><span>{tps.toFixed(2)} $</span>
                    </div>
                    <div className="flex justify-between text-slate-500 text-xs">
                      <span>TVQ (9.975%)</span><span>{tvq.toFixed(2)} $</span>
                    </div>
                    <div className="flex justify-between text-slate-900 pt-2 text-lg">
                      <span>Total TTC</span><span>{totalTTC.toFixed(2)} $</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Message au propriétaire</label>
                <textarea 
                  required 
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Bonjour, ce montant inclut le déplacement et le matériel de base..."
                  className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none transition-all h-32 resize-none shadow-sm" 
                />
              </div>
              
              <button type="submit" disabled={isSending || amountHT <= 0} className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-amber-500 hover:text-slate-900 transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 group">
                {isSending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} className="group-hover:translate-x-1 transition-transform" />}
                {isSending ? 'Création...' : 'Envoyer le devis'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
